import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { rnd } from './util.js';

// Shared unit primitives (cloned + baked on every add()).
const UNIT = {
  box: new THREE.BoxGeometry(1, 1, 1),
  sphere: new THREE.SphereGeometry(1, 10, 8),
  // Triangular prism: apex up, ridge along Z. Spans x in [-.866,.866], y in [-.5,1], z in [-.5,.5].
  prism: new THREE.CylinderGeometry(1, 1, 1, 3, 1).rotateX(-Math.PI / 2),
};
const cylCache = new Map();
const icoCache = new Map();

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _c = new THREE.Color();

/**
 * Tiny helper to assemble low-poly models out of primitives, baking transforms and
 * vertex colours so a whole house / tree / prop becomes ONE geometry (one draw call).
 *
 * box / cyl / cone / prism are anchored at their BASE centre (y = bottom);
 * ico / sphere are anchored at their centre. Rotations pivot around the anchor.
 * `o` = { rx, ry, rz, sx, sy, sz, jitter } (all optional; sx/sy/sz multiply the size).
 */
export class GeoBuilder {
  constructor() {
    this.parts = [];
  }

  /** Low-level: bake `geo` (unit-sized, centred) with transform + flat colour. */
  add(geo, x, y, z, color, o = {}, base = false) {
    const g = geo.index ? geo.toNonIndexed() : geo.clone();
    g.deleteAttribute('uv');
    if (base) g.translate(0, 0.5, 0);
    _e.set(o.rx || 0, o.ry || 0, o.rz || 0, 'YXZ');
    _q.setFromEuler(_e);
    _m.compose(_p.set(x, y, z), _q, _s.set(o.sx ?? 1, o.sy ?? 1, o.sz ?? 1));
    g.applyMatrix4(_m);
    if (!g.attributes.normal) g.computeVertexNormals();

    const n = g.attributes.position.count;
    const arr = new Float32Array(n * 3);
    if (Array.isArray(color)) _c.setRGB(color[0], color[1], color[2]);
    else _c.set(color);
    const jit = o.jitter || 0;
    for (let i = 0; i < n; i += 3) {
      // one brightness value per triangle -> faceted low-poly variation
      const k = jit ? 1 + (rnd() - 0.5) * jit : 1;
      for (let j = 0; j < 3; j++) {
        arr[(i + j) * 3] = _c.r * k;
        arr[(i + j) * 3 + 1] = _c.g * k;
        arr[(i + j) * 3 + 2] = _c.b * k;
      }
    }
    g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
    this.parts.push(g);
    return this;
  }

  box(w, h, d, x, y, z, color, o = {}) {
    return this.add(UNIT.box, x, y, z, color, { ...o, sx: w * (o.sx ?? 1), sy: h * (o.sy ?? 1), sz: d * (o.sz ?? 1) }, true);
  }

  /** Box anchored at its CENTRE (handy for rotated braces / slabs). */
  boxC(w, h, d, x, y, z, color, o = {}) {
    _e.set(o.rx || 0, o.ry || 0, o.rz || 0, 'YXZ');
    _p.set(0, (h * (o.sy ?? 1)) / 2, 0).applyEuler(_e);
    return this.box(w, h, d, x - _p.x, y - _p.y, z - _p.z, color, o);
  }

  /** Cylinder / truncated cone. Base anchored. rTop may be 0. */
  cyl(rTop, rBot, h, seg, x, y, z, color, o = {}) {
    const ratio = rTop / Math.max(rBot, 1e-5);
    const key = `${ratio.toFixed(4)}|${seg}`;
    let geo = cylCache.get(key);
    if (!geo) cylCache.set(key, (geo = new THREE.CylinderGeometry(ratio, 1, 1, seg, 1)));
    return this.add(geo, x, y, z, color, { ...o, sx: rBot * (o.sx ?? 1), sy: h * (o.sy ?? 1), sz: rBot * (o.sz ?? 1) }, true);
  }

  cone(r, h, seg, x, y, z, color, o = {}) {
    return this.cyl(0, r, h, seg, x, y, z, color, o);
  }

  /** Icosphere blob, centre anchored. */
  ico(r, detail, x, y, z, color, o = {}) {
    let geo = icoCache.get(detail);
    if (!geo) icoCache.set(detail, (geo = new THREE.IcosahedronGeometry(1, detail)));
    return this.add(geo, x, y, z, color, { ...o, sx: r * (o.sx ?? 1), sy: r * (o.sy ?? 1), sz: r * (o.sz ?? 1) });
  }

  sphere(r, x, y, z, color, o = {}) {
    return this.add(UNIT.sphere, x, y, z, color, { ...o, sx: r * (o.sx ?? 1), sy: r * (o.sy ?? 1), sz: r * (o.sz ?? 1) });
  }

  /** Triangular prism, apex up, ridge along Z. width (x), height (y), depth (z). Base anchored. */
  prism(w, h, d, x, y, z, color, o = {}) {
    return this.add(UNIT.prism, x, y, z, color, { ...o, sx: (w / 1.7320508) * (o.sx ?? 1), sy: (h / 1.5) * (o.sy ?? 1), sz: d * (o.sz ?? 1) }, true);
  }

  /** Append an arbitrary geometry (world-space already), tinted with one colour. */
  geometry(geo, color, o = {}) {
    return this.add(geo, 0, 0, 0, color, o, false);
  }

  build() {
    if (!this.parts.length) return null;
    const g = mergeGeometries(this.parts, false);
    this.parts.forEach((p) => p.dispose());
    this.parts = [];
    return g;
  }
}

/** Standard mesh factory for a vertex-coloured, flat-shaded prop. */
export function propMesh(geometry, opts = {}) {
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: opts.flat !== false,
    roughness: opts.roughness ?? 0.95,
    metalness: opts.metalness ?? 0,
  });
  const mesh = new THREE.Mesh(geometry, mat);
  mesh.castShadow = opts.cast !== false;
  mesh.receiveShadow = opts.receive !== false;
  return mesh;
}

/** Multiply vertex colours by a vertical gradient (baked pseudo-AO: dark at the bottom, light on top). */
export function shadeByHeight(geo, yMin, yMax, kMin = 0.6, kMax = 1.1) {
  const pos = geo.attributes.position, col = geo.attributes.color;
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, (pos.getY(i) - yMin) / (yMax - yMin || 1)));
    const k = kMin + (kMax - kMin) * t;
    col.setXYZ(i, col.getX(i) * k, col.getY(i) * k, col.getZ(i) * k);
  }
  col.needsUpdate = true;
  return geo;
}
